import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';

import Input from '@/shared/components/Input';
import '@/styles/design-system.css';

import {
  ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_EDIT,
} from '../../../constants';
import {
  ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE,
  OPERATOR_AND,
  OPERATOR_EQUAL,
} from '../../../dao/dbConstants';
import { searchEntityRecords } from '../../../utils/entityApiUtils';
import { showErrorMessage } from '@/shared/components/toast-system';
import { isContentPromptCategory } from './promptCategoryUtils';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : '—');

const SystemPrompts = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const result = await searchEntityRecords(ENTITY_NAME_BACK_OFFICE_PROMPT_TEMPLATE, {
          pageNumber: 1,
          pageSize: 1000,
          ignoreLimit: true,
          group: {
            operator: OPERATOR_AND,
            criteria: [{ fieldName: 'type', operator: OPERATOR_EQUAL, value: 'system' }],
          },
        });
        const all = result?.data?.list ?? [];
        setRows(
          all
            .filter(
              (row) =>
                !isContentPromptCategory(row.category) &&
                !row.masterCompanyTypeId &&
                !row.intent
            )
            .sort(
              (a, b) =>
                (a.category ?? '').localeCompare(b.category ?? '') ||
                (a.name ?? '').localeCompare(b.name ?? '')
            )
        );
      } catch (error) {
        console.error('SystemPrompts: failed to load', error);
        showErrorMessage('Failed to load system prompts.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = rows.filter((row) => {
    if (!query.trim()) return true;
    const needle = query.trim().toLowerCase();
    return (
      (row.category ?? '').toLowerCase().includes(needle) ||
      (row.name ?? '').toLowerCase().includes(needle)
    );
  });

  return (
    <div className="th !overflow-visible">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground">System Prompts</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Utility and internal prompts shared across all verticals. These prompts are not
            overridable per business vertical.
          </p>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-3">
          <Input
            icon={<Search className="size-4 text-muted-foreground" />}
            placeholder="Search prompts..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            wrapperClassName="w-64"
          />
        </div>

        {loading ? (
          <div className="py-16 text-center text-muted-foreground">Loading prompts...</div>
        ) : (
          <div className="overflow-hidden !rounded-xl !border !border-solid !border-[#cbd5e1] !bg-white !shadow-sm">
            <table className="w-full text-sm">
              <thead>
                <tr className="!border-b !border-solid !border-[#cbd5e1] !bg-[#f8fafc] text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  <th className="px-4 py-3">Category</th>
                  <th className="px-4 py-3">Prompt</th>
                  <th className="px-4 py-3 w-36">Last Updated</th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((row) => (
                  <tr
                    key={row.id}
                    onClick={() =>
                      navigate(`${ROUTE_BACK_OFFICE_PROMPT_TEMPLATE_EDIT}${row.id}`)
                    }
                    className="cursor-pointer !border-b !border-solid !border-[#e2e8f0] last:!border-b-0 transition-colors hover:!bg-[#eef2f6] text-foreground"
                  >
                    <td className="px-4 py-3">{row.category}</td>
                    <td className="px-4 py-3 font-medium">{row.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {formatDate(row.updatedOn)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <ChevronRight className="size-4" />
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      No prompts found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default SystemPrompts;